// src/onboarding/OnboardingAreas.jsx
// Phase 37 — extracted from src/App.jsx (Plan 37-03).
//
// Scope: Step 2 — confirm/add per-space areas. Renders a bird's-eye plot plan
// (OnbPlotPlan) that grows as presets are ticked or custom areas are added.
// Commits new areas to global state on continue.

import { useState, useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { ClearableInput } from '../App.jsx';
import OnbFrame from './OnbFrame.jsx';
import OnbPlotPlan from './OnbPlotPlan.jsx';
import {
  ONB_AREA_PATH_FALLBACK, ONB_AREA_PATHS,
  ONBOARD_PRESET_AREAS,
  GARDEN_SPACE_TO_AREA_LABEL, GARDEN_SPACE_TO_CUSTOM_AREA,
} from './constants.js';

export default function OnboardingAreas({areas, setAreas, gardenSpaces, onContinue, step = 2, total = 5}) {
  // Existing areas by lowercase name for case-insensitive match (D-13)
  const existingByName = useMemo(() => {
    const m = new Map();
    (areas || []).forEach(a => m.set(String(a.name || "").trim().toLowerCase(), a));
    return m;
  }, [areas]);
  const hasExisting = (areas || []).length > 0;

  // Selected preset labels (existing areas are pre-checked and disabled)
  const [selected, setSelected] = useState(() => {
    const s = new Set();
    // 1. Existing areas from re-run mode (D-13)
    ONBOARD_PRESET_AREAS.forEach(label => {
      if (existingByName.has(label.toLowerCase())) s.add(label);
    });
    // 2. Newly-derived from Step 1 gardenSpaces (gap 21-06)
    (Array.isArray(gardenSpaces) ? gardenSpaces : []).forEach(v => {
      const label = GARDEN_SPACE_TO_AREA_LABEL[v];
      if (label && !existingByName.has(label.toLowerCase())) s.add(label);
    });
    return s;
  });
  // Custom areas built by the user within the wizard — not committed until Continue
  const [customAreas, setCustomAreas] = useState(() => {
    const out = [];
    (Array.isArray(gardenSpaces) ? gardenSpaces : []).forEach(v => {
      const name = GARDEN_SPACE_TO_CUSTOM_AREA[v];
      if (name && !existingByName.has(name.toLowerCase()) && !out.some(c => c.name.toLowerCase() === name.toLowerCase())) {
        out.push({ name });
      }
    });
    return out;
  });
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customInput, setCustomInput] = useState("");

  // Only show presets the user ticked in the previous step — hide inactive
  // presets so the grid mirrors their confirmed spaces.
  // UAT2-GAP-01 (Phase 31.1 Plan 07): Filter is keyed on gardenSpaces alone.
  // Previously this OR'd with `existingByName.has(label)` which let INITIAL_AREAS-
  // seeded Front/Back leak through on first run even when the user unticked them.
  // Re-run mode is covered implicitly: if the user previously confirmed a preset
  // it is still in `gardenSpaces` (persisted via persistOnboardArray), so
  // `fromSpaces` contains it.
  const visiblePresets = useMemo(() => {
    const fromSpaces = new Set(
      (Array.isArray(gardenSpaces) ? gardenSpaces : [])
        .map(v => GARDEN_SPACE_TO_AREA_LABEL[v])
        .filter(Boolean)
    );
    return ONBOARD_PRESET_AREAS.filter(label => fromSpaces.has(label));
  }, [gardenSpaces]);

  const togglePreset = (label) => {
    // Existing areas (by name match) are locked on — cannot untick per D-13
    if (existingByName.has(label.toLowerCase())) return;
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(label)) n.delete(label); else n.add(label);
      return n;
    });
  };

  const handleAddCustom = () => {
    const raw = customInput;
    const trimmed = String(raw || "").trim().slice(0, 40);
    if (!trimmed) return;
    // Reject duplicates against existing area names or already-added customs (case-insensitive)
    const lower = trimmed.toLowerCase();
    if (existingByName.has(lower)) { setCustomInput(""); setShowCustomInput(false); return; }
    if (customAreas.some(c => c.name.toLowerCase() === lower)) { setCustomInput(""); return; }
    setCustomAreas(prev => [...prev, { name: trimmed }]);
    setCustomInput("");
    setShowCustomInput(false);
  };

  const removeCustom = (name) => {
    setCustomAreas(prev => prev.filter(c => c.name !== name));
  };

  const totalSelectedCount =
    // existing areas always count (kept in global state) + newly ticked presets + customs
    (hasExisting ? 1 : 0) + selected.size + customAreas.length;
  const canContinue = totalSelectedCount > 0;

  // Compute next numeric area id like "a3" by scanning existing ids (D-plan guidance)
  const nextAreaId = (existing) => {
    let maxN = 0;
    (existing || []).forEach(a => {
      const m = /^a(\d+)$/.exec(String(a.id || ""));
      if (m) { const n = parseInt(m[1], 10); if (n > maxN) maxN = n; }
    });
    return `a${maxN + 1}`;
  };

  const handleContinue = () => {
    if (!canContinue) return;
    // Merge newly ticked presets (only those NOT already present by name) + customAreas
    setAreas(prev => {
      const out = [...(prev || [])];
      const namesLower = new Set(out.map(a => String(a.name || "").trim().toLowerCase()));
      const pushNew = (name) => {
        const lower = name.toLowerCase();
        if (namesLower.has(lower)) return;
        out.push({ id: nextAreaId(out), name, subAreas: [] });
        namesLower.add(lower);
      };
      selected.forEach(label => {
        if (!existingByName.has(label.toLowerCase())) pushNew(label);
      });
      customAreas.forEach(c => pushNew(c.name));
      return out;
    });
    onContinue();
  };

  // D-03 — derive plot areas for OnbPlotPlan. Selected (or existing) areas
  // render solid; visible-but-unticked presets render as pending/dashed.
  // Custom areas resolve via ONB_AREA_PATH_FALLBACK.
  // Note: `label` is deliberately OMITTED here — the pill row below already
  // shows the names, and the OnbPlotPlan <text> labels would collide with
  // screen.getByText(/^Back Garden$/i) and screen.queryByText('Balcony') in
  // Phase 31.1 Plan 07 tests (which don't distinguish between a span label
  // and an SVG text child). Shapes + colours + pending-dash alone carry the
  // visual signal on this screen.
  const plotAreas = useMemo(() => {
    const out = [];
    const seen = new Set();
    const pushArea = (name, { pending = false } = {}) => {
      const lower = name.toLowerCase();
      if (seen.has(lower)) return;
      seen.add(lower);
      const geom = ONB_AREA_PATHS[name] || ONB_AREA_PATH_FALLBACK;
      out.push({
        name,
        // label intentionally omitted — see note above.
        path: geom.path, lx: geom.lx, ly: geom.ly,
        color: geom.color, hatch: geom.hatch,
        pending,
      });
    };
    // Existing areas first (always solid — they're locked in).
    (areas || []).forEach(a => pushArea(a.name, { pending: false }));
    // Visible presets: solid if in `selected`, pending otherwise.
    visiblePresets.forEach(label => {
      if (existingByName.has(label.toLowerCase())) return;
      pushArea(label, { pending: !selected.has(label) });
    });
    // Customs: solid (they're user-confirmed within the wizard).
    customAreas.forEach(c => pushArea(c.name, { pending: false }));
    return out;
  }, [areas, visiblePresets, selected, customAreas, existingByName]);

  return (
    <OnbFrame step={step} total={total} ground="dawn">
      <div className="onb-content">
        <div style={{ height: 14 }}/>
        <p className="onb-eyebrow">Drawing your plan</p>
        <h1 className="onb-heading">A <em>bird's-eye</em> view of your plot.</h1>
        <p className="onb-sub">
          {(Array.isArray(gardenSpaces) && gardenSpaces.length > 0)
            ? "Here's how your spaces sit together. Tap to add or remove a plot — or sketch in something we missed."
            : "Pick your top-level spaces — garden, balcony, allotment. We'll map the smaller spots inside in the next step."}
        </p>

        {/* D-03 — plot plan grows as areas are confirmed */}
        <div style={{ marginTop: 12 }}>
          <OnbPlotPlan areas={plotAreas}/>
        </div>

        {hasExisting && (
          <div style={{ marginTop: 12, background: "var(--c-tint)", border: "1px solid var(--c-border)", borderRadius: 12, padding: "10px 12px", fontSize: "var(--font-small)", color: "var(--c-text-2)" }}>
            We have set up these spaces, do you want to add any more?
          </div>
        )}

        {/* Pill grid — UNCHANGED from Phase 31.1 Plan 07 (UAT2-GAP-01, UAT2-GAP-02). */}
        {/* DO NOT restyle these pills with .onb-tile or any other class — */}
        {/* src/App.test.jsx asserts the inline style and hidden <input type=checkbox>. */}
        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
          {visiblePresets.map(label => {
            const isExisting = existingByName.has(label.toLowerCase());
            const checked = isExisting || selected.has(label);
            return (
              <label
                key={label}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  borderRadius: 12,
                  border: `1px solid ${checked ? "var(--c-heading)" : "var(--c-border)"}`,
                  background: checked ? "var(--c-tint)" : "var(--c-surface)",
                  padding: "12px 16px",
                  minHeight: 44,
                  cursor: isExisting ? "default" : "pointer",
                  opacity: isExisting ? 0.8 : 1,
                  fontSize: "var(--font-body)",
                  color: "var(--c-text)",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={isExisting}
                  onChange={() => togglePreset(label)}
                  style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0, height: 0 }}
                />
                {/* UAT2-GAP-02 (Phase 31.1 Plan 07): inline leading tick before label. */}
                {checked && (
                  <span aria-hidden="true" style={{ color: "var(--c-heading)", display: "flex", flexShrink: 0 }}>
                    <Check size={16}/>
                  </span>
                )}
                <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
              </label>
            );
          })}
          {customAreas.map(c => (
            <label
              key={`custom-${c.name}`}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                borderRadius: 12,
                border: "1px solid var(--c-heading)",
                background: "var(--c-tint)",
                padding: "12px 16px",
                minHeight: 44,
                fontSize: "var(--font-body)",
                color: "var(--c-text)",
              }}
            >
              <input type="checkbox" checked readOnly style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0, height: 0 }}/>
              {/* UAT2-GAP-02 (Phase 31.1 Plan 07): inline layout — tick before label, X after. */}
              <span aria-hidden="true" style={{ color: "var(--c-heading)", display: "flex", flexShrink: 0 }}>
                <Check size={16}/>
              </span>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
              <button
                type="button"
                aria-label={`Remove ${c.name}`}
                onClick={(e) => { e.preventDefault(); removeCustom(c.name); }}
                style={{ background: "none", border: "none", color: "var(--c-text-3)", cursor: "pointer", padding: 2, display: "flex", flexShrink: 0 }}
              >
                <X size={14}/>
              </button>
            </label>
          ))}
        </div>

        {/* Add custom — UNCHANGED from Phase 31.1 */}
        <div style={{ marginTop: 12 }}>
          {!showCustomInput ? (
            <button
              type="button"
              onClick={() => setShowCustomInput(true)}
              style={{ background: "none", border: "none", color: "var(--c-text-2)", fontSize: "var(--font-small)", cursor: "pointer", fontFamily: "inherit", padding: "4px 0" }}
            >
              + Add custom space
            </button>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <ClearableInput
                type="text"
                value={customInput}
                maxLength={40}
                placeholder="e.g. Side gravel"
                onChange={e => setCustomInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") { e.preventDefault(); handleAddCustom(); }
                  else if (e.key === "Escape") { setShowCustomInput(false); setCustomInput(""); }
                }}
                wrapperStyle={{ flex: 1, minWidth: 0 }}
                style={{ background: "var(--c-bg)", border: "1px solid var(--c-border)", color: "var(--c-text)", fontFamily: "inherit", fontSize: "var(--font-body)", borderRadius: 20, padding: "8px 16px", outline: "none", width: "100%" }}
                autoFocus
              />
              <button type="button" onClick={handleAddCustom}
                      style={{ background: "var(--c-heading)", color: "#fff", border: "none", borderRadius: 20, padding: "8px 16px", fontSize: "var(--font-small)", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Add
              </button>
              <button type="button" onClick={() => { setShowCustomInput(false); setCustomInput(""); }}
                      style={{ background: "none", border: "none", color: "var(--c-text-3)", fontSize: "var(--font-small)", cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
            </div>
          )}
        </div>

        {!canContinue && (
          <div style={{ fontSize: "var(--font-small)", color: "var(--c-danger)", marginTop: 8 }}>
            Select at least one space to continue.
          </div>
        )}

        <div className="push-bottom">
          <button type="button" className="onb-cta" onClick={handleContinue} disabled={!canContinue}>
            Next — inside each space →
          </button>
        </div>
      </div>
    </OnbFrame>
  );
}
